import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useOrganization } from '@clerk/clerk-react'

import {
  ASSIGNMENT_LOGIC_QUERY,
  EXCLUSION_LOGIC_QUERY,
  LAST_ASSIGNMENT_CHANGE_QUERY,
  LAST_ASSIGNMENT_RUN_QUERY,
  LAST_EXCLUSION_CHANGE_QUERY,
  dutyClientKeys,
} from '@/lib/queryKeys'
import type { ChatEvent } from '@/types'

/**
 * The David tools that can change a duty's client assignment page: saving
 * either rule, and applying them. Previews and dry runs change nothing, and
 * David's SQL tool cannot write duties or their members. Names as registered
 * in `hoku/tools/registry.py`.
 */
const DUTY_ASSIGNMENT_WRITE_TOOLS: ReadonlySet<string> = new Set([
  'save_duty_assignment_logic',
  'save_duty_exclusion_logic',
  'apply_duty_assignment',
])

/**
 * Whether a finished David turn called a tool that can change a duty's client
 * assignment page, so only those turns pay for refetching it. A failed call
 * still counts: refreshing after it is cheap, and missing a change is not.
 */
export function changedDutyAssignment(events: readonly ChatEvent[]): boolean {
  return events.some(
    (event) =>
      event.type === 'tool_call' &&
      DUTY_ASSIGNMENT_WRITE_TOOLS.has(event.toolName)
  )
}

/** Everything on a duty's client assignment page that a rule edit or a run changes. */
const CLIENT_ASSIGNMENT_QUERIES: ReadonlySet<unknown> = new Set([
  ASSIGNMENT_LOGIC_QUERY,
  EXCLUSION_LOGIC_QUERY,
  LAST_ASSIGNMENT_RUN_QUERY,
  LAST_ASSIGNMENT_CHANGE_QUERY,
  LAST_EXCLUSION_CHANGE_QUERY,
])

/**
 * Refreshes a duty's client assignment page: its rules, their last run and
 * last changes, and its client list. Given a duty id it covers that duty;
 * without one it covers every duty in the org, for a caller that can't tell
 * which duty changed (a finished David turn that saved or applied a duty's
 * rules; the caller checks that with `changedDutyAssignment`).
 * Only queries on screen refetch; the rest are just marked stale.
 */
export function useRefreshDutyAssignment() {
  const queryClient = useQueryClient()
  const { organization } = useOrganization()
  const orgId = organization?.id

  return useCallback(
    async (dutyId?: string) => {
      if (!orgId) return
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: dutyId
            ? dutyClientKeys.all(orgId, dutyId)
            : dutyClientKeys.org(orgId),
        }),
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            queryKey[0] === 'duty' &&
            queryKey[1] === orgId &&
            (dutyId === undefined || queryKey[2] === dutyId) &&
            CLIENT_ASSIGNMENT_QUERIES.has(queryKey[3]),
        }),
      ])
    },
    [queryClient, orgId]
  )
}
